
/*
* A popup module to handle popups display, aria-attributes and focus management based on defined
* `data-` and `hidden` attributes.
*
* Usage instructions:
*
* 1. Include the module file on your page (preferably in the end)
* 2. Initialize the module with: popup.init();
* 3. Add an `id` to each `flix-popup` element you wish the module to manage
* 4. Connect the target to the popup by passing the popup id to the target's `data-popup` attribute
* 5. Ideally initialize the popup with a hidden attribute on
*/

/*global define*/
/*eslint no-undef: ["error", { "typeof": true }] */

(function (root, factory) {
  if ( typeof define === 'function' && define.amd ) {
    // AMD. Register as an anonymous module.
    define([], factory(root));
  } else if ( typeof exports === 'object' ) {
    module.exports = factory(root);
  } else {
    // Browser globals (root is window)
    root.popup = factory();
  }
})(typeof global !== 'undefined' ? global : this.window || this.global, function() {
  'use strict';

  // Default values for the selector for the target elements that we should apply the handlers.
  const TARGET_SELECTOR = 'data-popup';

  // List of selectors to query focusable elements, credits go to:
  // https://github.com/KittyGiraudel/focusable-selectors/blob/main/index.js
  const FOCUSABLE_SELECTORS = [
    'a[href]:not([tabindex^="-"])',
    'input:not([type="hidden"]):not([type="radio"]):not([tabindex^="-"]):not(:disabled)',
    'input[type="radio"]:not([tabindex^="-"]):not(:disabled)',
    'select:not([tabindex^="-"]):not(:disabled)',
    'textarea:not([tabindex^="-"]):not(:disabled)',
    'button:not([tabindex^="-"]):not(:disabled)',
    'details > summary:first-of-type:not([tabindex^="-"])',
    '[tabindex]:not([tabindex^="-"])',
  ];

  const popup = {};

  /**
   * Toggles popup based on the current value of `hidden` attribute
   * @param {Element} target
   * @param {Element} popupElement
   * @private
   */
  function togglePopup(target, popupElement) {
    if (popupElement.hasAttribute('hidden')) {
      showPopup(target, popupElement);
    } else {
      hidePopup(target, popupElement);
    }
  };

  /**
   * Shows popup and focuses the first focusable element.
   * Also allows content to be focused if it's too long.
   *
   * @param {Element} target
   * @param {Element} popupElement
   * @private
   */
  function showPopup(target, popupElement) {
    const interactiveElements = popupElement.querySelectorAll(FOCUSABLE_SELECTORS.join(','));
    const firstFocusableElement = popupElement.querySelector(`#${target.getAttribute('data-firstFocusable')}`) || interactiveElements[0];

    popupElement.removeAttribute('hidden');
    firstFocusableElement && firstFocusableElement.focus();

    // Adds tabindex to the popup content if the content is too long and has a scroll bar.
    // Tabindex allows keyboard users to focus on the content area and use arrow keys to scroll.
    const popupContent = popupElement.querySelector('[class$="__content"]');
    if (popupContent && (popupContent.scrollHeight > popupContent.clientHeight)) {
      popupContent.setAttribute('tabindex', '0');
    }

    setTimeout(() => {
      const activeModifier = buildActiveModifier(popupElement);
      popupElement.classList.add(activeModifier);
      target.setAttribute('aria-expanded', 'true');
    }, 1);
  };

  /**
   * Hides popup and sets target expanded to false if "click" event.
   * @param {Element} target
   * @param {Element} popupElement
   * @private
   */
  function hidePopup(target, popupElement) {
    const activeModifier = buildActiveModifier(popupElement);
    popupElement.classList.remove(activeModifier);
    target.setAttribute('aria-expanded', 'false');
    target.focus();

    setTimeout(() => {
      popupElement.setAttribute('hidden', '');
    }, 250);
  };

  /**
   * Builds the active modifier for a given popup or panel element.
   *
   * @param {Element} popupElement - The popup element to build the active modifier for.
   * @return {string} The active modifier for the popup element.
   * @private
   */
  function buildActiveModifier(popupElement) {
    // either popup or a panel is supported right now
    // this checks for a panel and fallbacks for popup if none is found
    let componentClassName = popupElement.classList.contains('flix-panel') ? 'flix-panel' : 'flix-popup';
    return `${componentClassName}--active`;
  }

  /**
   * Attach close event handlers for Escape key and close buttons found within the popup.
   * @param {Element} target
   * @param {Element} popupElement
   * @private
   */
  function attachCloseHandlers(target, popupElement) {
    const closeButtonDeprecated = popupElement.querySelector('[class*="__close"]');
    if (closeButtonDeprecated) {
      closeButtonDeprecated.addEventListener('click', function() {
        if (closeButtonDeprecated.getAttribute('data-closePopup') === null) {
          // eslint-disable-next-line no-console
          console.warn('`popup.js` plugin deprecation warning (12.5.0): The close handler added to buttons with `__close` class name is deprecated and will be removed in version 13. To make a button close the popup or panel, add the `data-closePopup` attribute to it.');
          hidePopup(target, popupElement);
        }
      });
    }

    const closeButtonCurrent = popupElement.querySelectorAll('[data-closePopup]');
    closeButtonCurrent.forEach((closeButton) => {
      closeButton.addEventListener('click', function() {
        hidePopup(target, popupElement);
      });
    });

    popupElement.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        hidePopup(target, popupElement);
      }
    });
  }

  /**
   * Creates a tab trap within the popup stopping users from tabbing away from it.
   * Will cycle through a list of interactive elements that can be overwritten with `data-firstFocusable` and `data-lastFocusable` attributes on the target.
   *
   * @param {Element} target
   * @param {Element} popupElement
   * @private
   */
  function attachTabTrap(target, popupElement) {
    const interactiveElements = popupElement.querySelectorAll(FOCUSABLE_SELECTORS.join(','));
    const firstFocusableId = target.getAttribute('data-firstFocusable');
    const lastFocusableId = target.getAttribute('data-lastFocusable');

    let firstFocusableElement = popupElement.querySelector(`#${firstFocusableId}`);
    let lastFocusableElement = popupElement.querySelector(`#${lastFocusableId}`);

    if (firstFocusableElement === null) {
      if (firstFocusableId !== null) {
        // eslint-disable-next-line no-console
        console.info('`popup.js` plugin warning: Cannot find element with id ' + firstFocusableId + ' supplied to data-firstFocusable attribute. Using the first element found from a list of focusable selectors instead.');
      }
      firstFocusableElement = interactiveElements[0];
    }

    if (lastFocusableElement === null) {
      if (lastFocusableElement !== null) {
        // eslint-disable-next-line no-console
        console.info('`popup.js` plugin warning: Cannot find element with id ' + lastFocusableId + ' supplied to data-lastFocusable attribute. Using the last element found from a list of focusable selectors instead.');
      }
      lastFocusableElement = interactiveElements[interactiveElements.length - 1];
    }

    firstFocusableElement.addEventListener('keydown', function (event) {
      if (event.shiftKey && event.key === 'Tab') {
        event.preventDefault();
        lastFocusableElement && lastFocusableElement.focus();
      }
    });

    lastFocusableElement.addEventListener('keydown', function (event) {
      if (!event.shiftKey && event.key === 'Tab') {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    });
  }

  // Public Initializer
  popup.init = function() {
    const popupTargets = document.querySelectorAll('['+ TARGET_SELECTOR +']');

    popupTargets.forEach(function (target) {
      const popupId = target.getAttribute(TARGET_SELECTOR);
      const popupElement = document.getElementById(popupId);

      if (popupElement === null) {
        // eslint-disable-next-line no-console
        console.error('popup.js plugin error: Cannot find element with id ' + popupId + ' for target ' + target);
      } else {
        popupElement.setAttribute('role', 'dialog');

        const popupOverlay = popupElement.querySelector('.flix-overlay');
        // Initializes modal features if popup has overlay
        if (popupOverlay) {
          popupElement.setAttribute('aria-modal', 'true');

          attachTabTrap(target, popupElement);

          popupOverlay.addEventListener('click', function() {
            hidePopup(target, popupElement);
          });
        }

        target.addEventListener('click', function() {
          togglePopup(target, popupElement);
        });

        attachCloseHandlers(target, popupElement);
      }
    });
  };

  return popup;
});
